// © 2026 KaraokeClubs. All rights reserved.
// Crowd-sourced karaoke hours — effortless, one-glance editor.
//
// A single row of 7 day pills sits under the schedule. Tap a day → a sleek
// time strip slides out; tap a time → done. Each day shows live consensus
// progress (dots filling toward the threshold) so it feels like a game, not a
// form. Once enough people agree, the schedule auto-applies — no admin.

"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { t } from "@/lib/i18n";
import type { Locale } from "@/lib/types";

interface Props {
  venueId: string;
  locale: Locale;
}

// 0=Mon … 6=Sun, matching the API + Python weekday().
const DAYS: { wd: number; key: string }[] = [
  { wd: 0, key: "mon" },
  { wd: 1, key: "tue" },
  { wd: 2, key: "wed" },
  { wd: 3, key: "thu" },
  { wd: 4, key: "fri" },
  { wd: 5, key: "sat" },
  { wd: 6, key: "sun" },
];

const START_TIMES = ["18:00", "19:00", "20:00", "21:00", "22:00"];

interface Tally {
  open: string | null;
  close: string | null;
  votes: number;
  total: number;
}

export default function HoursEditor({ venueId, locale }: Props) {
  const [tallies, setTallies] = useState<Record<number, Tally>>({});
  const [threshold, setThreshold] = useState(3);
  const [activeDay, setActiveDay] = useState<number | null>(null);
  const [voted, setVoted] = useState<Record<number, string | null>>({});
  const [busy, setBusy] = useState(false);
  const [appliedDay, setAppliedDay] = useState<number | null>(null);

  useEffect(() => {
    fetch(`/api/hours?venue_id=${encodeURIComponent(venueId)}`)
      .then((r) => r.json())
      .then((d) => {
        setTallies(d.tallies ?? {});
        if (d.threshold) setThreshold(d.threshold);
      })
      .catch(() => {});
  }, [venueId]);

  async function vote(weekday: number, open: string | null) {
    if (busy) return;
    setBusy(true);
    // Optimistic: mark immediately so the tap feels instant.
    setVoted((v) => ({ ...v, [weekday]: open }));
    try {
      const res = await fetch("/api/hours", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_id: venueId,
          weekday,
          open,
          close: open ? "02:00" : null,
        }),
      });
      const d = await res.json();
      if (res.ok) {
        setTallies(d.tallies ?? tallies);
        if (d.applied) {
          setAppliedDay(weekday);
          setTimeout(() => setAppliedDay(null), 2200);
        }
      }
    } catch {
      /* best-effort */
    } finally {
      setBusy(false);
      setTimeout(() => setActiveDay(null), 260);
    }
  }

  return (
    <div className="mt-1 mb-6">
      <p className="text-[12px] text-zinc-500 mb-2.5">{t(locale, "hours_help_hint")}</p>

      {/* Day pills — always visible, instantly tappable */}
      <div className="flex gap-1.5">
        {DAYS.map(({ wd, key }) => {
          const tally = tallies[wd];
          const votes = tally?.votes ?? 0;
          const isActive = activeDay === wd;
          const myVote = voted[wd];
          const mine = myVote !== undefined;
          const justApplied = appliedDay === wd;
          const reached = votes >= threshold;

          return (
            <button
              key={wd}
              onClick={() => setActiveDay(isActive ? null : wd)}
              disabled={busy}
              className={`group flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border
                          transition-all active:scale-95 touch-manipulation disabled:opacity-60
                          ${isActive
                            ? "bg-violet-600/25 border-violet-500/50"
                            : mine || reached
                              ? "bg-violet-500/10 border-violet-500/25"
                              : "bg-zinc-800/50 border-white/[0.06] hover:border-violet-500/30"}`}
            >
              <span className={`text-[11px] font-bold uppercase tracking-wide
                                ${isActive || mine || reached ? "text-violet-300" : "text-zinc-400"}`}>
                {t(locale, key).slice(0, 3)}
              </span>

              {/* state line: time, or consensus dots */}
              {justApplied ? (
                <motion.svg
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24" strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </motion.svg>
              ) : mine ? (
                <span className="text-[9px] tabular-nums text-violet-300 font-semibold">
                  {myVote === null ? "✕" : myVote}
                </span>
              ) : tally && tally.open !== null && votes > 0 ? (
                <span className="text-[9px] tabular-nums text-zinc-400 font-medium">{tally.open}</span>
              ) : (
                <span className="text-[9px] text-zinc-600">+</span>
              )}

              {/* consensus progress dots */}
              <span className="flex gap-0.5">
                {Array.from({ length: threshold }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-1 h-1 rounded-full transition-colors
                                ${i < votes ? "bg-violet-400" : "bg-white/[0.12]"}`}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      {/* Time strip — slides out for the active day */}
      <AnimatePresence>
        {activeDay !== null && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="mt-2.5 p-3 rounded-2xl bg-zinc-900/70 border border-white/[0.06]">
              <p className="text-[11px] text-zinc-500 mb-2">
                {t(locale, "hours_pick_start")}{" "}
                <span className="text-violet-300 font-semibold">
                  {t(locale, DAYS[activeDay].key)}
                </span>
              </p>
              <div className="flex gap-1.5">
                {START_TIMES.map((time) => (
                  <motion.button
                    key={time}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => vote(activeDay, time)}
                    disabled={busy}
                    className="flex-1 py-2.5 rounded-xl bg-violet-500/10 border border-violet-500/25
                               text-[13px] font-bold text-violet-200 tabular-nums
                               transition hover:bg-violet-500/25 disabled:opacity-60"
                  >
                    {time}
                  </motion.button>
                ))}
              </div>
              <button
                onClick={() => vote(activeDay, null)}
                disabled={busy}
                className="w-full mt-1.5 py-2 rounded-xl bg-zinc-800/80 border border-white/[0.06]
                           text-[12px] font-medium text-zinc-400
                           transition hover:text-zinc-200 active:scale-[0.98] disabled:opacity-60"
              >
                {t(locale, "hours_no_karaoke")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-[10px] text-zinc-600 mt-2">
        {t(locale, "hours_consensus_note").replace("{n}", String(threshold))}
      </p>
    </div>
  );
}
